import clsx from 'clsx';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import ChevronRightIcon from '@/assets/icons/chevron-right.svg';
import ErrorIcon from '@/assets/icons/exclamation-circle.svg';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { POOL } from '@/types/channels';
import { formatShortDate } from '@/utils/date-util';

interface ChannelSummaryProps {
  channelKey: string;
  setIsExpanded?: (isExpanded: boolean) => void;
}

export function ChannelSummary({
  channelKey,
  setIsExpanded,
}: ChannelSummaryProps) {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();
  const { hookForm } = useJobPostingContext();
  const {
    watch,
    setValue,
    formState: { errors },
  } = hookForm;

  const hasErrors = errors?.channels?.[channelKey];

  const channelData = watch(`channels.${channelKey}`);
  const {
    slug: channelSlug,
    name: channelName,
    displayTitle,
    vonqDetails,
    isFree,
  } = channelData;
  const isPool = channelSlug === POOL;
  const startsAt = watch(`channels.${channelKey}.startsAt`);
  const endAt = watch(`channels.${channelKey}.endAt`);
  const totalChannelBudget = watch(`channels.${channelKey}.totalChannelBudget`);
  const isExpanded = watch(`channels.${channelKey}.isExpanded`);

  const toggleExpanded = () => {
    if (setIsExpanded) {
      setIsExpanded(!isExpanded);
      return;
    }

    setValue(`channels.${channelKey}.isExpanded`, !isExpanded, {
      shouldDirty: false,
    });
  };

  const displayBudget = useMemo(() => {
    if (isPool || isFree)
      return intl.formatMessage({
        defaultMessage: 'Free',
        description: 'Free budget',
      });

    if (!totalChannelBudget) return '-';

    return formatCurrency(totalChannelBudget);
  }, [formatCurrency, intl, isPool, isFree, totalChannelBudget]);

  return (
    <button
      type="button"
      onClick={toggleExpanded}
      className={clsx(
        'border-fx-neutral-200 hover:bg-fx-neutral-50 mb-2 w-full rounded-lg border bg-white p-6 shadow-sm',
        hasErrors && 'border-fx-rose',
      )}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-6">
          <ChannelIcon
            channelSlug={channelSlug}
            variant="large"
            channelName={channelName}
            displayTitle={displayTitle}
            className="min-w-0 flex-1"
            logoSquareUrl={vonqDetails?.logoSquareUrl}
            logoRectangleUrl={vonqDetails?.logoRectangleUrl}
          />
          <Typography variant="body1">
            {formatShortDate(startsAt) ?? 'Today'} -
            {endAt ? formatShortDate(endAt) : 'Ongoing'}
          </Typography>
        </div>

        <div className="flex items-center gap-4">
          {hasErrors && (
            <Tooltip
              content={intl.formatMessage({
                defaultMessage:
                  'Some fields are incomplete or have invalid entries. Please review and fix the highlighted fields.',
                description: 'Error message for required fields',
              })}
            >
              <img src={ErrorIcon} alt="Error" />
            </Tooltip>
          )}
          <Typography variant="h4" className="text-fx-neutral-600">
            {displayBudget}
          </Typography>
          <img src={ChevronRightIcon} alt="Chevron right" />
        </div>
      </div>
    </button>
  );
}
